import React, { useEffect, useId, useState } from "react";
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DANGER, FONT_BODY, FONT_HEADING, RADIUS, dialogPaperProps } from "../styles";
import { useDialogFocusReturn } from "../lib/focusReturn";

/**
 * The danger button, in both themes. Not the palette's error colour: the dark theme's is a light red,
 * and white 14px text on it measures 3.68:1, under AA's 4.5. DANGER, the brand red the unsaved-changes
 * dialog used before this one, holds 8.14:1 in both. Hover darkens it rather than switching to the
 * palette's `error.dark`. The theme's own error colour is left alone: every other error surface uses it.
 */
const DANGER_BUTTON_SX = {
  bgcolor: DANGER,
  color: "common.white",
  "&:hover": { bgcolor: DANGER, filter: "brightness(0.92)" },
} as const;

/** What an action came to. `error` is shown as written, so it is written for the admin reading it. */
export interface ActionResult {
  success: boolean;
  error?: string;
}

/** What a confirmed action may return. Nothing at all counts as done: some handlers report no result. */
export type ActionOutcome = ActionResult | void;

export interface ConfirmDialogProps {
  open: boolean;
  /** The question, naming the person or item it acts on. */
  title: React.ReactNode;
  /** The consequence. A string is set as body text; anything else renders as given. */
  children: React.ReactNode;
  /** Names the action. Never "OK". */
  confirmLabel: string;
  /** Ends someone's access or loosens protection: the button takes the error colour. */
  danger?: boolean;
  busy?: boolean;
  /** Shown on the button while busy instead of a spinner, e.g. "Transferring…". */
  busyLabel?: string;
  /** Shown above the body. The dialog stays open, so a refusal is read where it happened. */
  error?: string | null;
  onClearError?: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  cancelLabel?: string;
  /** Focus Cancel on open, so Enter leaves things as they are. */
  focusCancel?: boolean;
  maxWidth?: "xs" | "sm";
  /** Holds the button off, e.g. until a choice the body asks for is made. */
  confirmDisabled?: boolean;
}

/** One paragraph of a confirmation's body, in the console's dialog text. `primary` is the effect line. */
export const ConfirmText: React.FC<{ children: React.ReactNode; primary?: boolean; "data-testid"?: string }> = ({
  children, primary = false, "data-testid": testId,
}) => (
  <Typography
    data-testid={testId}
    sx={(t) => ({
      fontFamily: FONT_BODY,
      fontSize: "14px",
      color: primary ? t.palette.text.primary : t.palette.text.secondary,
      mb: 1.5,
      "&:last-child": { mb: 0 },
    })}
  >
    {children}
  </Typography>
);

/**
 * ENG-3351 — the console's confirmation dialog. When to confirm, and when to offer Undo instead, is
 * the rule in this package's CLAUDE.md ("Confirm or undo").
 *
 * Controlled: the caller owns `open`, `busy` and `error`. For an action described once and run from
 * the dialog, `ConfirmationDialog` below does that bookkeeping.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, children, confirmLabel, danger = false, busy = false, busyLabel, error = null, onClearError,
  onConfirm, onCancel, cancelLabel = "Cancel", focusCancel = false, maxWidth = "sm", confirmDisabled = false,
}) => {
  const theme = useTheme();
  const titleId = useId();
  const bodyId = useId();
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={dialogPaperProps}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
    >
      <DialogTitle id={titleId} sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={onClearError} sx={{ mb: 2, borderRadius: RADIUS.input, fontFamily: FONT_BODY }}>
            {error}
          </Alert>
        )}
        <div id={bodyId}>{typeof children === "string" ? <ConfirmText>{children}</ConfirmText> : children}</div>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onCancel}
          disabled={busy}
          autoFocus={focusCancel}
          sx={{ fontFamily: FONT_BODY, textTransform: "none", color: theme.palette.text.secondary, borderRadius: RADIUS.pill }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
          variant="contained"
          color={danger ? "error" : "primary"}
          // A spinner has no name; the button keeps its own while it spins.
          aria-label={busy && !busyLabel ? confirmLabel : undefined}
          sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: "none", borderRadius: RADIUS.pill, ...(danger ? DANGER_BUTTON_SX : {}) }}
        >
          {busy ? (busyLabel ?? <CircularProgress size={18} color="inherit" />) : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** A confirmation described once: what to ask, and what to run when the admin agrees. */
export interface Confirmation {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Runs on confirm. A failure keeps the dialog open with the reason in it. */
  action: () => Promise<ActionOutcome> | ActionOutcome;
  /** Said when the action fails without a reason of its own. */
  failed?: string;
  /**
   * ENG-3897: the body says why the action can't be taken, and the button is held, as the invite
   * dialog holds Send for a full family. Only Cancel closes it.
   */
  confirmDisabled?: boolean;
  /**
   * ENG-3502: the control this was opened from, when focus is not on it as the dialog opens: a menu
   * item's menu has closed, so the caller names the menu's button. Otherwise the focused control is
   * taken, when it is in the page.
   */
  returnFocusFrom?: HTMLElement | null;
}

/**
 * A `Confirmation` in the dialog: runs its action, closes when it lands, and on failure stays open with
 * the reason in it. Lifted from Managed access (ENG-3347), which built it for its own actions.
 *
 * Hold the confirmation in state rather than building it inline per render. The last one is kept
 * while the dialog animates closed, so its words do not blank out mid-transition.
 */
export const ConfirmationDialog: React.FC<{ confirmation: Confirmation | null; onClose: () => void }> = ({
  confirmation, onClose,
}) => {
  const [last, setLast] = useState<Confirmation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (confirmation) setLast(confirmation);
  }, [confirmation]);
  // The live one while open, so the first frame is never blank; the last one only while closing.
  const shown = confirmation ?? last;

  // ENG-3502: an action often takes away the control it was opened from (a deactivated member's menu, a
  // revoked row), and focus fell to the page when the dialog closed. Given back when it closes, either
  // way (lib/focusReturn).
  const giveFocusBack = useDialogFocusReturn(confirmation != null, confirmation?.returnFocusFrom);

  const close = () => {
    if (busy) return;
    setError(null);
    onClose();
    giveFocusBack();
  };

  const confirm = async () => {
    if (!confirmation || busy || confirmation.confirmDisabled) return;
    setBusy(true);
    setError(null);
    let result: ActionOutcome;
    try {
      result = await confirmation.action();
    } catch (err) {
      result = { success: false, error: err instanceof Error ? err.message : undefined };
    }
    setBusy(false);
    if (!result || result.success) {
      onClose();
      giveFocusBack();
    } else setError(result.error || confirmation.failed || "That didn't work.");
  };

  return (
    <ConfirmDialog
      open={confirmation != null}
      title={shown?.title}
      confirmLabel={shown?.confirmLabel ?? ""}
      danger={shown?.danger}
      confirmDisabled={shown?.confirmDisabled}
      busy={busy}
      error={error}
      onClearError={() => setError(null)}
      onConfirm={() => void confirm()}
      onCancel={close}
    >
      {shown?.body}
    </ConfirmDialog>
  );
};
